import { useRef, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import Seo from '../components/Seo'
import MarketingNav from '../components/MarketingNav'
import MarketingFooter from '../components/MarketingFooter'
import PageHero from '../components/ds/PageHero'
import Section from '../components/ds/Section'
import Button from '../components/ds/Button'
import Field from '../components/ds/Field'
import AudienceChoice from '../components/ds/AudienceChoice'
import HumanCheck, { humanCheckConfigured } from '../components/ds/HumanCheck'
import { SECTION_HERO_XML } from '../../../shared/onboardingSectionArt.js'
import { API } from '../lib/api'
import { trackEvent } from '../lib/analytics'
import { dashboardUrl } from '../lib/dashboardUrl'

// ── /signup ──────────────────────────────────────────────────────────────────
// Name, email, and which side of the marketplace you are on. That is the whole
// page, and it is deliberately the whole page.
//
// ⛔ IT MUST NEVER GROW INTO ONBOARDING. Everything after this point, identity
// documents, date of birth, National Insurance number, bank details, the legal
// name that comes off the ID check, belongs in the app, because a browser has
// nowhere safe to keep any of it. Commit 235dd8f removed onboarding from the web
// for that reason and docs/WEB-APP-BOUNDARY.md is the standing rule.
//
// ⚠️ The name typed here is a MARKETING LABEL on a lead. It never becomes
// anybody’s legal name: that has one source, the identity check.
//
// The audience answer decides three things, which is why it is asked rather than
// guessed: which welcome email arrives, which app store link is in it, and
// whether the next screen offers to carry on in the browser.
//
// ⚠️ ASKED ONLY WHEN WE DO NOT KNOW. Every signup CTA on the site carries its
// trunk in ?for=work|cover (a "Join TempDash" on the Find work side cannot mean
// anything else), so a visitor arriving with it is never shown the chooser:
// asking a question they have just answered by clicking is friction on the page
// that converts. Bare /signup (typed, bookmarked, linked from outside) is the
// only case that still asks. If you add a signup CTA, give it ?for=.

// ⚖️ Soft opt-in (founder 2026-10-03, PECR reg. 22): news and offers about our
// own services go out unless the person says no, so this is an UNTICKED OPT-OUT.
// The version travels with the form and the server stores the wording from its
// own map (servers/onboarding/src/routes/marketing.js MARKETING_TEXT_VERSIONS).
// ⛔ Change the words, change the version, and add the new pair to that map in
// the same PR. Never reuse a version for different words.
const OPT_OUT_VERSION = 'optout-2026-10'
const OPT_OUT_TEXT =
  'Don’t email me TempDash news and offers. You can also unsubscribe from any email.'

// Article 13 UK GDPR: say at capture what we will do with it, in the words we
// will later have to stand behind. ⚠️ Lockstep with CAPTURE_NOTICE_TEXT in
// servers/onboarding/src/routes/marketing.js.
const NOTICE =
  'We’ll email you to help you finish signing up, and now and then with TempDash news and offers.'

// The privacy notice for the side of the marketplace they are joining.
const PRIVACY_PATH = { work: '/legal/worker-privacy', cover: '/legal/privacy' }

export default function SignUp() {
  const [params]   = useSearchParams()
  const navigate   = useNavigate()
  const fromTrunk  = params.get('for') === 'cover' ? 'cover' : params.get('for') === 'work' ? 'work' : null

  // Derived, not copied into state, so a change of ?for= can never leave a
  // stale answer behind. `chosen` only matters while the chooser is showing.
  const [chosen, setChosen] = useState('work')
  const audience = fromTrunk ?? chosen
  const [name,  setName]  = useState('')
  const [email, setEmail] = useState('')
  const [optOut, setOptOut] = useState(false)
  const [errors, setErrors] = useState({})
  const [busy, setBusy] = useState(false)
  const human = useRef(null)

  async function onSubmit(e) {
    e.preventDefault()
    const next = {}
    if (!name.trim()) next.name = 'Tell us what to call you.'
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) next.email = "That doesn’t look like an email address."
    setErrors(next)
    if (Object.keys(next).length) return

    setBusy(true)
    // 🤖 The server refuses a sign up without a human check token
    // (lib/turnstile requireHuman). This waits for the check if it is still running.
    const turnstileToken = await human.current?.token()
    if (humanCheckConfigured() && !turnstileToken) {
      setErrors({ form: 'Complete the check above, then sign up.' })
      setBusy(false)
      return
    }
    try {
      const res = await fetch(`${API}/api/marketing/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          audience,
          marketingOptOut: optOut,
          consentTextVersion: OPT_OUT_VERSION,
          turnstileToken: turnstileToken || undefined,
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        // ⛔ A token is single use, so every failed submit needs a fresh one.
        human.current?.reset()
        setErrors({ form: body.code === 'human-check-required'
          ? 'Complete the check above, then sign up.'
          : body.retryInSeconds
          ? "That’s a few too many tries. Give it a minute and go again."
          : 'We could not take that just now. Try again in a moment.' })
        setBusy(false)
        return
      }
      trackEvent('sign_up', { audience })
      navigate(`/welcome?for=${audience}`, { replace: true })
    } catch {
      // Offline, or the API is unreachable. Say what happened, not what we guess.
      human.current?.reset()
      setErrors({ form: 'We could not reach TempDash. Check your connection and try again.' })
      setBusy(false)
    }
  }

  return (
    <div className="bg-canvas">
      <Seo
        title="Sign up"
        description="Your name, your email, and whether you’re looking for work or need cover. We’ll send you everything you need to get started."
        path="/signup"
      />
      <MarketingNav />

      {/* ⚠️ The ONE main landmark. Without it every <section> on the page
          sits outside any landmark and a screen reader user navigating by
          landmark cannot reach the content at all. The skip link in
          MarketingNav targets this id. */}
      <main id="main" tabIndex={-1} className="outline-none">

      <PageHero
        width="text"
        art={SECTION_HERO_XML.frontDoor}
        title="Start here."
        lead="A couple of details. We’ll email you the rest."
      />

      <Section ground="canvas" width="text">
        <form onSubmit={onSubmit} noValidate className="space-y-7">
          {!fromTrunk && (
            <>
              <AudienceChoice value={audience} onChange={setChosen} />
              {/* ⚠️ The prerendered /signup/index.html is built with no query
                  string, so it always contains the chooser, and it sits on
                  screen until the client mounts (createRoot, not hydrate: the
                  client re-renders from scratch). This hides it during that
                  window when the URL already names the audience. It runs only
                  when the static HTML is parsed; React never executes it. */}
              <script
                dangerouslySetInnerHTML={{ __html:
                  'if(/[?&]for=(work|cover)(&|$)/.test(location.search))document.currentScript.previousElementSibling.hidden=true' }}
              />
            </>
          )}

          <Field
            id="name"
            label="Your name"
            value={name}
            error={errors.name}
            autoComplete="given-name"
            onChange={(e) => setName(e.target.value)}
          />

          <Field
            id="email"
            label="Your email"
            type="email"
            inputMode="email"
            value={email}
            error={errors.email}
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
          />

          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={optOut}
              onChange={(e) => setOptOut(e.target.checked)}
              className="mt-1 h-[18px] w-[18px] shrink-0 rounded border-ink/25 accent-action-fill"
            />
            <span className="text-[15px] leading-relaxed text-ink-muted">{OPT_OUT_TEXT}</span>
          </label>

          <HumanCheck ref={human} />

          {errors.form && (
            <p role="alert" className="rounded-tile bg-danger-tint px-4 py-3 text-[15px] text-danger">
              {errors.form}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-4">
            <Button type="submit" disabled={busy}>
              {busy ? 'One moment' : 'Sign up'}
            </Button>
            <p className="text-[14px] text-ink-muted">Takes about twenty seconds.</p>
          </div>

          {/* The other door, for someone who landed on the wrong side. A quiet
              sentence, not the chooser: the link they clicked already told us
              who they are, and this is only the way out if it was wrong.
              Business goes where every Get cover CTA goes (the dashboard's
              /register); worker stays here with the trunk flipped. */}
          {fromTrunk && (
            <p className="text-[15px] text-ink-muted">
              {audience === 'work' ? (
                <>Need cover for a workplace?{' '}
                  <a href={dashboardUrl('/register')} className="text-link underline underline-offset-4">
                    Sign up as a business instead
                  </a>
                </>
              ) : (
                <>Looking for shifts?{' '}
                  <Link to="/signup?for=work" className="text-link underline underline-offset-4">
                    Sign up as a worker instead
                  </Link>
                </>
              )}
            </p>
          )}

          <p className="border-t border-hairline pt-6 text-[14px] leading-relaxed text-ink-muted">
            {NOTICE}{' '}
            <Link to={PRIVACY_PATH[audience]} className="text-link underline underline-offset-4">
              How we handle your details
            </Link>
          </p>
        </form>
      </Section>

      </main>

      <MarketingFooter />
    </div>
  )
}
